/**
 * LibraryHubSlideOver — the chat-first Library Hub shell.
 *
 * Replaces the modal Dialog at addin/src/variant-c/components/
 * LibraryHub.tsx (legacy) with a slide-over from the right edge,
 * leaving an 18px strip of chat visible on the left as the "obvious
 * close" affordance per Spec 34 §"Implementation milestones" and
 * the design handoff README.
 *
 * Owns:
 *   - Backdrop fade-in (180ms) + panel slide-right (220ms cubic-
 *     bezier(0.2, 0.8, 0.2, 1)) animations.
 *   - Internal mode dispatch — library-list / empty / creating /
 *     uploading / list / detail.
 *   - A real back-stack: the header back button mirrors the writer's
 *     drill path (library-list → list → detail).
 *   - Header chrome with sign-off badge + close ×.
 *   - ESC-to-close + initial focus trap on the close button.
 *   - Backdrop click → onClose.
 *
 * Does NOT own:
 *   - Library list fetch (parent passes ``libraries`` + ``library``).
 *   - Per-mode content (each mode is a separate component the
 *     shell renders by name).
 *   - The selected library state — the shell forwards row-body
 *     selection and chevron-based inspection as separate intents.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { brandColors, fonts, inkColors, surfaceColors } from "../../tokens";
import { useHubNavigation } from "../hooks/useHubNavigation";
import { HubNavShell } from "./HubNavShell";
import { HubTransition } from "./HubTransition";

// Per Spec 34 §"Implementation milestones" + the design handoff
// README §"Interaction notes": backdrop fade 180ms, slide panel
// 220ms cubic-bezier(0.2, 0.8, 0.2, 1). Kept as literal durations
// here rather than tokens because they're animation-specific to
// this component (no other surface uses these values).
const BACKDROP_FADE_MS = 180;
const PANEL_SLIDE_MS = 220;
import type { InventoryResolveEvent, RunAuditLibrary } from "../../../api/client";
import type { Library, Workspace } from "../../../types";
import { AuditLibraryPanel } from "../../components/inspector/AuditLibraryPanel";
import type { DocumentRow } from "../types";
import { HubCreating, type LibraryScope } from "./HubCreating";
import { HubDetail } from "./HubDetail";
import { HubEmpty } from "./HubEmpty";
import { HubLibraryList, isEmptyLibrary } from "./HubLibraryList";
import { HubList, type HubListTabs } from "./HubList";
import { HubUploading } from "./HubUploading";

/**
 * Hub modes. ``library-list`` is the workspace-level index (M8 —
 * shipped alongside the per-library inspect chevron in the
 * QuickPicker); ``empty`` survives for the no-libraries-yet case in
 * the workspace. The remaining modes are the per-library views.
 */
export type HubMode = "library-list" | "empty" | "creating" | "uploading" | "list" | "detail";

export interface LibraryHubSlideOverProps {
  /** Slide-over visibility. False → component returns null
   *  (no orphan DOM, no animation cost). */
  open: boolean;
  /** Close affordances: backdrop click, header ×, ESC. */
  onClose: () => void;
  /** Active library — drives the header in per-library modes.
   *  ``null`` is valid (e.g. in ``library-list``, ``empty``,
   *  ``creating``). */
  library: Library | null;
  /** All libraries in the workspace — rendered in ``library-list``
   *  mode. */
  libraries: Library[];
  /** Initial mode. Once open, the shell may navigate internally
   *  (e.g. ``library-list → list`` via the row click,
   *  ``list → detail`` via a doc click, etc.). */
  mode: HubMode;
  /** Per-doc rows the unified Hub renders — same array drives
   *  ``uploading`` (filter to in-flight) and ``list`` (full set).
   *  The shell does not slice; sub-views handle filtering. */
  rows: DocumentRow[];
  /** Workspace name for the header subtitle when no library is
   *  active (library-list, creating, empty modes). */
  workspaceName: string;
  /** Workspaces available to the writer — powers the library-list
   *  switcher. Omit to hide it. */
  workspaces?: Workspace[];
  /** Active workspace id — the switcher's selected option. */
  selectedWorkspaceId?: string | null;
  /** Fires on a workspace change in the library-list switcher. Parent
   *  persists it and cascades the library re-fetch. */
  onSelectWorkspace?: (workspaceId: string) => void;
  /** Known page totals per library id for the library-list cards'
   *  "N files · M pages" line (see ``totalPageCount``). Omit, or leave a
   *  library out, and its card shows files only. */
  libraryPageCounts?: Record<string, number | null | undefined>;
  /** "Create & add files" submission — parent handles the API call
   *  (create + select + inventory refresh). The shell awaits it, then
   *  lands the writer on the standard library ``list`` view. May throw
   *  on failure so the shell can keep the writer on the create form. */
  onCreateLibrary: (input: { name: string; scope: LibraryScope }) => void | Promise<void>;
  /** True while a create call is in flight; passed through to
   *  HubCreating's submit-button disabled state. */
  creating?: boolean;
  /** Row-body pick from ``library-list`` — parent handles the
   *  normal selection flow (chat emit, close, orchestration). */
  onSelectLibrary: (libraryId: string) => void;
  /** When set, renders a "Continue without a library" action in the
   *  ``library-list`` view. Used by the build-on-existing-document flow, where
   *  a source library is optional — the audit library is bootstrapped from the
   *  document's own content. Absent for the normal fresh-draft flow (a library
   *  is required to draft from). */
  onSkipLibrary?: () => void;
  /** Chevron pick from ``library-list`` — parent updates the
   *  inspected library while the shell stays open. */
  onInspectLibrary: (libraryId: string) => void;
  /** Drag-drop / "+ Add files" → parent's upload flow. Wired from
   *  HubEmpty (drag-drop only — its CTA is "Create library", not
   *  "Add") and from HubList (both drag-drop AND the explicit
   *  "+ Add files" button). */
  onAddFiles?: (files: File[]) => void;
  /** "Create artefact" from HubList's "+" menu → parent opens the
   *  artefact builder for the current library. */
  onCreateArtefact?: () => void;
  /** Click on a row in HubList → opens HubDetail. The shell flips
   *  ``mode`` to ``detail`` and remembers the selected doc. */
  onPickDoc?: (docId: string) => void;
  /** Delete a document from the library. */
  onDeleteDoc?: (row: DocumentRow) => void;
  /** Download a document's original stored file. */
  onDownloadDoc?: (row: DocumentRow) => void;
  /** Re-read the current library (document list + processing status). */
  onRefreshLibrary?: () => void;
  /** Run the full, on-demand document labelling for the current library. */
  onClassifyLibrary?: () => void;
  /** "✓ Verify & save" in HubDetail — parent handles persistence;
   *  per Spec 34 DR-5, sign-off is the row-level ``verified`` flag
   *  and editing tags drops it (the parent reapplies on save). */
  onVerifyDoc?: (input: {
    doc_id: string;
    tags: Record<string, string | null>;
    source_doctype?: string;
  }) => void;
  /** Available source doctype options for the HubDetail change dropdown.
   *  Derived from the library's target_doctype by the shell. */
  docTypeOptions?: readonly string[];
  /** True while a verify call is in flight. */
  savingVerify?: boolean;
  /** True while the inventory is being fetched/classified. */
  loading?: boolean;
  /** Current progress message from the SSE stream (e.g. "Labelled foo.pdf"). */
  loadingMessage?: string;
  /**
   * Latest raw SSE event from `streamLibraryInventory` — passed
   * through to HubList to enable the streaming intelligence UX.
   * Zero-touch to the SSE contract.
   */
  resolveEvent?: InventoryResolveEvent | null;
  /** Active run id, or null when no run is open. The Source↔Audit toggle only
   *  appears inside a specific library (``list``/``detail``) that is the run's
   *  source library (``auditLibrary.source_library_id``) AND only when the run
   *  has an audit library, so the workspace-wide library list never shows it. */
  runId?: string | null;
  /** The active run's dynamic audit library (precedent-aware audit). Fetched by
   *  the parent; the toggle/panel self-hide when this is absent or disabled. */
  auditLibrary?: RunAuditLibrary | null;
  /** Re-fetch the audit library (wired to the panel's Refresh button). */
  onRefreshAuditLibrary?: () => void;
  /** Uploads and Failed tabs for the inspected library, beside its documents. */
  libraryTabs?: HubListTabs;
}

const useStyles = makeStyles({
  backdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(15, 10, 30, 0.18)",
    zIndex: 80,
    animationName: {
      from: { opacity: 0 },
      to: { opacity: 1 },
    },
    animationDuration: `${BACKDROP_FADE_MS}ms`,
    animationTimingFunction: "ease-out",
  },
  panel: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: "calc(100% - 18px)",
    height: "100vh",
    backgroundColor: surfaceColors.paper,
    borderLeft: `1px solid ${surfaceColors.line}`,
    boxShadow: "-12px 0 28px -10px rgba(15, 10, 30, 0.18)",
    zIndex: 81,
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    animationName: {
      from: { transform: "translateX(8%)", opacity: 0 },
      to: { transform: "translateX(0)", opacity: 1 },
    },
    animationDuration: `${PANEL_SLIDE_MS}ms`,
    animationTimingFunction: "cubic-bezier(0.2, 0.8, 0.2, 1)",
  },

  modePlaceholder: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px 24px",
    textAlign: "center",
    fontFamily: fonts.body,
    fontSize: "11px",
    color: inkColors.ink3,
  },
  // Source↔Audit segmented toggle, shown below the header only when the active
  // run has an audit library.
  toggleBar: {
    display: "flex",
    gap: "4px",
    padding: "8px 12px 0",
    flex: "0 0 auto",
  },
  toggleBtn: {
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "6px",
    background: surfaceColors.paper,
    color: inkColors.ink3,
    fontFamily: fonts.body,
    fontSize: "11px",
    fontWeight: 600,
    padding: "5px 12px",
    cursor: "pointer",
  },
  toggleBtnActive: {
    background: "#1565C0",
    color: "#FFFFFF",
    border: "1px solid #1565C0",
  },
  auditPane: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    padding: "10px 12px 24px",
  },
  skipRow: {
    padding: "12px 16px 20px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  skipButton: {
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    textAlign: "left",
    fontSize: "13px",
    fontWeight: 600,
    color: "var(--colorBrandForeground1, #5b4bd6)",
    ":hover": { textDecoration: "underline" },
  },
  skipHint: {
    fontSize: "11px",
    color: "var(--colorNeutralForeground3, #6b6b6b)",
    lineHeight: 1.4,
  },
  // Picker footer: the one place a library is committed (file-picker style).
  footer: {
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 14px",
    borderTop: `1px solid ${surfaceColors.line}`,
    backgroundColor: "#FFFFFF",
  },
  footerNote: {
    flex: 1,
    minWidth: 0,
    fontSize: "11px",
    lineHeight: 1.4,
    color: inkColors.ink3,
    fontFamily: fonts.body,
  },
  footerBtn: {
    flexShrink: 0,
    maxWidth: "60%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    backgroundColor: brandColors.primary,
    color: "#FFFFFF",
    border: "none",
    borderRadius: "4px",
    padding: "7px 14px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: fonts.body,
    cursor: "pointer",
    ":disabled": {
      backgroundColor: surfaceColors.line,
      color: inkColors.ink4,
      cursor: "default",
    },
    ":focus-visible": {
      outline: `2px solid ${brandColors.primary}`,
      outlineOffset: "2px",
    },
  },
});

export const LibraryHubSlideOver: React.FC<LibraryHubSlideOverProps> = ({
  open,
  onClose,
  library,
  libraries,
  mode: initialMode,
  rows,
  workspaceName,
  workspaces,
  selectedWorkspaceId,
  onSelectWorkspace,
  libraryPageCounts,
  onCreateLibrary,
  creating = false,
  onSelectLibrary,
  onSkipLibrary,
  onInspectLibrary,
  onAddFiles,
  onCreateArtefact,
  onPickDoc,
  onVerifyDoc,
  onDeleteDoc,
  onDownloadDoc,
  onRefreshLibrary,
  onClassifyLibrary,
  docTypeOptions,
  savingVerify = false,
  loading = false,
  loadingMessage = "Loading library…",
  resolveEvent = null,
  runId = null,
  auditLibrary = null,
  onRefreshAuditLibrary,
  libraryTabs,
}) => {
  const styles = useStyles();
  const { innerMode, selectedDocId, direction, back, navigateTo, navigateBack, selectDoc } = useHubNavigation(
    initialMode,
    open,
  );

  // ESC closes. Effect-bound listener so we don't leak when the
  // shell un-mounts.
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  // Initial focus trap — focus the close button on open so screen
  // readers + keyboard users land at a sensible anchor. Per
  // principles §1: small, single-responsibility focus management.
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (open) closeBtnRef.current?.focus();
  }, [open]);

  // Create-form submission state. Owned here so the form stays
  // mounted (with an inline error) if the create call fails, and only
  // advances to the library view on success.
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Source↔Audit view toggle — local state, deliberately NOT a HubMode, so the
  // header back-stack (library-list → list → detail) is unaffected. Reset to the
  // source view each time the slide-over opens, and whenever the writer leaves
  // the library or switches to another one (so a later library never opens
  // straight onto an audit view).
  const [view, setView] = useState<"source" | "audit">("source");
  const inspectedLibraryId = library?.id ?? null;
  const insideLibrary = innerMode === "list" || innerMode === "detail";
  useEffect(() => {
    if (open) setView("source");
  }, [open, inspectedLibraryId, insideLibrary]);

  // Library-list picker state, held here (not in HubLibraryList) so it
  // survives a trip into a library and back: the marked library, the search
  // text and the list's scroll position. Reset each time the slide-over opens.
  // ``committedId`` is the library already in use when the slide-over opened.
  const [markedId, setMarkedId] = useState<string | null>(null);
  const [librarySearch, setLibrarySearch] = useState("");
  const libraryScrollRef = useRef(0);
  const [committedId, setCommittedId] = useState<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: snapshot the library in use only when opening
  useEffect(() => {
    if (!open) return;
    setMarkedId(library?.id ?? null);
    setCommittedId(library?.id ?? null);
    setLibrarySearch("");
    libraryScrollRef.current = 0;
  }, [open]);

  const handleInspect = useCallback(
    (id: string) => {
      setMarkedId(id);
      onInspectLibrary(id);
      navigateTo("list");
    },
    [onInspectLibrary, navigateTo],
  );

  const handleCreateFromEmpty = useCallback(() => {
    setCreateError(null);
    navigateTo("creating");
  }, [navigateTo]);

  const handleCreateSubmit = useCallback(
    async (input: { name: string; scope: LibraryScope }) => {
      // Parent owns the create-library API call (create + select +
      // inventory refresh). Await it so ``selectedLibraryId`` is set
      // before we navigate — then land on the standard ``list`` view,
      // the SAME page shown when opening an existing library, where
      // documents are listed and uploaded (in-flight uploads show
      // progress inline as merged rows). On failure, stay on the form
      // and surface the error instead of stranding the writer.
      setCreateSubmitting(true);
      setCreateError(null);
      try {
        await onCreateLibrary(input);
        navigateTo("list");
      } catch (err) {
        setCreateError(err instanceof Error ? err.message : "Couldn't create the library. Please try again.");
      } finally {
        setCreateSubmitting(false);
      }
    },
    [onCreateLibrary, navigateTo],
  );

  const handleCreateCancel = useCallback(() => {
    setCreateError(null);
    navigateTo("library-list");
  }, [navigateTo]);

  if (!open) return null;

  // The Source↔Audit toggle appears only inside a specific library that is the
  // open run's source library, AND only when that run has an audit library
  // (feature enabled + a ref exists). An audit library is built for one source
  // library, so it never shows on the workspace-wide list or in other libraries.
  const auditAvailable = Boolean(
    runId &&
      auditLibrary?.enabled &&
      auditLibrary?.audit_library &&
      insideLibrary &&
      inspectedLibraryId &&
      auditLibrary.source_library_id === inspectedLibraryId,
  );
  const effectiveView: "source" | "audit" = auditAvailable ? view : "source";

  // Stable key for HubTransition — "detail-<id>" so switching docs
  // triggers the slide animation even when the mode stays "detail".
  const transitionKey = innerMode === "detail" && selectedDocId ? `detail-${selectedDocId}` : innerMode;

  // Filename for the detail breadcrumb.
  const selectedDocFilename =
    innerMode === "detail" && selectedDocId ? (rows.find((r) => r.doc_id === selectedDocId)?.filename ?? null) : null;

  // Footer: commit the marked library (library-list) or the open one (list).
  // An empty library can't be used yet; the note says what to do instead.
  let footer: React.ReactNode = null;
  // Nothing usable yet (only empty libraries): the list's own guidance says
  // what to do, so no footer.
  if (innerMode === "library-list" && libraries.some((l) => !isEmptyLibrary(l))) {
    const marked = libraries.find((l) => l.id === markedId) ?? null;
    const empty = !!marked && isEmptyLibrary(marked);
    footer = (
      <div className={styles.footer} data-testid="hub-picker-footer">
        <span className={styles.footerNote}>
          {!marked
            ? "Select a library to draft from."
            : empty
              ? `${marked.name} is empty. Press Upload on it to add documents first.`
              : marked.id === committedId
                ? "In use for this document."
                : null}
        </span>
        <button
          type="button"
          className={styles.footerBtn}
          disabled={!marked || empty}
          onClick={() => marked && onSelectLibrary(marked.id)}
          data-testid="hub-picker-use"
        >
          {marked ? `Use ${marked.name}` : "Use library"}
        </button>
      </div>
    );
  } else if (innerMode === "list" && library && library.id !== committedId) {
    const hasDocs = !isEmptyLibrary(library) || (!loading && rows.length > 0);
    footer = (
      <div className={styles.footer} data-testid="hub-picker-footer">
        <span className={styles.footerNote}>
          {hasDocs ? null : "Upload your source documents here, then use this library."}
        </span>
        <button
          type="button"
          className={styles.footerBtn}
          disabled={!hasDocs}
          onClick={() => onSelectLibrary(library.id)}
          data-testid="hub-picker-use"
        >
          Use this library
        </button>
      </div>
    );
  }

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={onClose}
        onKeyDown={() => {}}
        aria-hidden="true"
        data-testid="hub-backdrop"
      />
      <dialog open className={styles.panel} aria-label="Library Hub" data-testid="hub-panel">
        <HubNavShell
          mode={innerMode}
          workspaceName={workspaceName}
          library={library}
          docFilename={selectedDocFilename}
          back={back}
          closeBtnRef={closeBtnRef}
          onBack={navigateBack}
          onClose={onClose}
        />

        {auditAvailable ? (
          <div className={styles.toggleBar} role="tablist" aria-label="Library view">
            <button
              type="button"
              role="tab"
              aria-selected={effectiveView === "source"}
              className={`${styles.toggleBtn} ${effectiveView === "source" ? styles.toggleBtnActive : ""}`}
              onClick={() => setView("source")}
            >
              Source library
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={effectiveView === "audit"}
              className={`${styles.toggleBtn} ${effectiveView === "audit" ? styles.toggleBtnActive : ""}`}
              onClick={() => setView("audit")}
            >
              Audit library
            </button>
          </div>
        ) : null}

        {effectiveView === "audit" ? (
          <div className={styles.auditPane} data-testid="hub-audit-pane">
            <AuditLibraryPanel data={auditLibrary} onRefresh={onRefreshAuditLibrary} runId={runId} />
          </div>
        ) : (
        <HubTransition modeKey={transitionKey} direction={direction}>
          {innerMode === "library-list" && (
            <HubLibraryList
              libraries={libraries}
              activeLibraryId={markedId}
              onPickLibrary={setMarkedId}
              onInspectLibrary={handleInspect}
              searchTerm={librarySearch}
              onSearchChange={setLibrarySearch}
              scrollTop={libraryScrollRef.current}
              onScrollTopChange={(v) => {
                libraryScrollRef.current = v;
              }}
              onCreateLibrary={() => navigateTo("creating")}
              workspaces={workspaces}
              selectedWorkspaceId={selectedWorkspaceId}
              onSelectWorkspace={onSelectWorkspace}
              libraryPageCounts={libraryPageCounts}
            />
          )}
          {innerMode === "library-list" && onSkipLibrary && (
            <div className={styles.skipRow}>
              <button
                type="button"
                className={styles.skipButton}
                onClick={onSkipLibrary}
                data-testid="hub-skip-library"
              >
                Continue without a library →
              </button>
              <span className={styles.skipHint}>
                Building on this document — a source library is optional. You can connect one later.
              </span>
            </div>
          )}
          {innerMode === "empty" && <HubEmpty onCreate={handleCreateFromEmpty} />}
          {innerMode === "creating" && (
            <HubCreating
              onCreate={handleCreateSubmit}
              onCancel={handleCreateCancel}
              submitting={createSubmitting || creating}
              error={createError}
            />
          )}
          {innerMode === "uploading" && <HubUploading rows={rows} />}
          {innerMode === "list" && (
            <HubList
              rows={rows}
              loading={loading}
              loadingMessage={loadingMessage}
              resolveEvent={resolveEvent}
              onPickDoc={(row) => {
                if (row.doc_id) {
                  selectDoc(row.doc_id);
                  onPickDoc?.(row.doc_id);
                }
              }}
              onAddFiles={onAddFiles ?? (() => {})}
              onCreateArtefact={onCreateArtefact}
              onDeleteDoc={onDeleteDoc}
              onDownloadDoc={onDownloadDoc}
              onRefresh={onRefreshLibrary}
              onClassify={onClassifyLibrary}
              tabs={libraryTabs}
            />
          )}
          {innerMode === "detail" &&
            (() => {
              const selected = rows.find((r) => r.doc_id === selectedDocId);
              if (!selected) {
                // Race or stale selection — bounce back to list.
                return (
                  <div className={styles.modePlaceholder} data-testid="hub-detail-placeholder">
                    Document no longer in this library.
                  </div>
                );
              }
              return (
                <HubDetail
                  row={selected}
                  onBack={navigateBack}
                  onVerify={onVerifyDoc ?? (() => {})}
                  docTypeOptions={docTypeOptions}
                  saving={savingVerify}
                />
              );
            })()}
        </HubTransition>
        )}
        {effectiveView === "source" && footer}
      </dialog>
    </>
  );
};

export default LibraryHubSlideOver;
